
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    country: '',
    productInterest: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    // Validate textarea length
    if (formData.message.length > 500) {
      alert('Message must be 500 characters or less');
      setIsSubmitting(false);
      return;
    }

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d3vjjumg28apfrolkje0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: formDataToSend
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          country: '',
          productInterest: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Hello! I'm interested in your trading services. Could you please provide more information?"
    );
    const whatsappUrl = `https://wa.me/971555219159?text=${message}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <section
      id="contact"
      className="py-20 relative bg-cover bg-center bg-no-repeat"
      style={{
        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), url('https://public.readdy.ai/ai/img_res/71b5760dd8673ab0c7a5b0849560b949.jpg')`
      }}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Get in Touch
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Ready to start your international trading journey? Contact us today
            for a personalized quote and discover how we can help grow your
            business globally.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
              Request a Quote
            </h3>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="contact-form"
              className="space-y-6"
            >
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="your@email.com"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Company Name
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="Your company"
                  />
                </div>
                <div>
                  <label
                    htmlFor="phone"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
                    placeholder="+971 55 123 4567"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="country"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Country *
                  </label>
                  <div className="relative">
                    <select
                      id="country"
                      name="country"
                      required
                      value={formData.country}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus-border-transparent text-sm appearance-none bg-white"
                    >
                      <option value="">Select your country</option>
                      {/* ... huge list of country options ... */}
                      <option value="United States">United States</option>
                      <option value="United Kingdom">United Kingdom</option>
                      {/* (the rest of the options remain unchanged) */}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>
                <div>
                  <label
                    htmlFor="productInterest"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Product Interest
                  </label>
                  <div className="relative">
                    <select
                      id="productInterest"
                      name="productInterest"
                      value={formData.productInterest}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm appearance-none bg-white"
                    >
                      <option value="">Select category</option>
                      <option value="Educational Toy">Educational Toy</option>
                      <option value="Electronic Plastic Toy">Electronic Plastic Toy</option>
                      <option value="Hobby/Model Construction">Hobby/Model Construction</option>
                      <option value="Electronic Games">Electronic Games</option>
                      <option value="Hi-tech Toy">Hi-tech Toy</option>
                      <option value="Non-Electronic Plastic Toy">Non-Electronic Plastic Toy</option>
                      <option value="Custom Sourcing">Custom Sourcing</option>
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none"></i>
                  </div>
                </div>
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  Message *
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  value={formData.message}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus-border-transparent text-sm resize-vertical"
                  placeholder="Tell us about your requirements, quantities, and any specific needs..."
                ></textarea>
                <div className="text-right text-sm text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full"
                  size="lg"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Sending...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Send Quote Request
                    </>
                  )}
                </Button>

                <button
                  type="button"
                  onClick={handleWhatsAppClick}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-whatsapp-line mr-2 text-lg"></i>
                  WhatsApp Us
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-800">
                  <i className="ri-check-circle-line mr-2"></i>
                  Thank you! Your quote request has been sent successfully. We'll get back to you within 24 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
                  <i className="ri-error-warning-line mr-2"></i>
                  Sorry, there was an error sending your message. Please try again or contact us directly.
                </div>
              )}
            </form>
          </div>

          {/* Contact Information */}
          <div className="space-y-8">
            <div>
              <h3 className="text-2xl font-bold text-white mb-6">
                Contact Information
              </h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-red-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">
                      Our Location
                    </h4>
                    <p className="text-gray-200">
                      Ajman, United Arab Emirates<br />
                      Strategic location with access to major ports
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-green-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">
                      WhatsApp Support
                    </h4>
                    <p className="text-gray-200">Available during business hours</p>
                    <button
                      onClick={handleWhatsAppClick}
                      className="mt-2 text-green-400 hover:text-green-300 font-medium cursor-pointer"
                    >
                      Start WhatsApp Chat →
                    </button>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">
                      Business Hours
                    </h4>
                    <p className="text-gray-200">
                      Sunday - Thursday: 9:00 AM - 6:00 PM GST<br />
                      Friday - Saturday: Closed
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <img
                      src="https://readdy.ai/api/search-image?query=Gulf%20Conformity%20Mark%20GCC%20logo%20official%20certification%20mark%2C%20professional%20quality%20assurance%20symbol%2C%20regulatory%20compliance%20badge%2C%20clean%20white%20background%2C%20official%20government%20standard%20mark&width=48&height=48&seq=gcm1&orientation=squarish"
                      alt="Gulf Conformity Mark"
                      className="w-8 h-8 object-contain"
                    />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-white mb-1">
                      Gulf Conformity Mark
                    </h4>
                    <p className="text-gray-200">
                      GCC certified products<br />
                      Meeting Gulf standards
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <h4 className="text-xl font-bold text-white mb-4">
                Why Choose PGVHarmony?
              </h4>
              <div className="space-y-3">
                <div className="flex items-center text-gray-200">
                  <i className="ri-check-line text-green-400 mr-3"></i>
                  <span>5+ years of international trading experience</span>
                </div>
                <div className="flex items-center text-gray-200">
                  <i className="ri-check-line text-green-400 mr-3"></i>
                  <span>Established partnerships with global suppliers</span>
                </div>
                <div className="flex items-center text-gray-200">
                  <i className="ri-check-line text-green-400 mr-3"></i>
                  <span>Competitive pricing and quality assurance</span>
                </div>
                <div className="flex items-center text-gray-200">
                  <i className="ri-check-line text-green-400 mr-3"></i>
                  <span>Reliable shipping and logistics network</span>
                </div>
                <div className="flex items-center text-gray-200">
                  <i className="ri-check-line text-green-400 mr-3"></i>
                  <span>Dedicated customer support team</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
